import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectCurrentUser,
  selectUserRole,
  selectCompanyName,
  selectHasSuperAdminAccess,
} from '../../store/slices/authSlice';

// Exact roles for "Client/Company". NOTE: deliberately NOT a startsWith('CLIENT_')
// check — that prefix also matches CLIENT_VENDOR_ADMIN, which is a separate
// OmniDrive-created intermediate-approver role, not a Client/Company user,
// and should not see this page.
const CLIENT_COMPANY_ROLES = new Set(['CLIENT_ADMIN', 'CLIENT_USER', 'CLIENT_VIEWER']);

/* ─────────────────────────────────────────────────────────────────────────
   BOB's Dashboard
   Visible to: CLIENT_ADMIN / CLIENT_USER / CLIENT_VIEWER (Client/Company)
               and Super Admin-equivalent platform roles.

   New, standalone page — does not modify any existing dashboard, route,
   or role logic. Wired in via net-new route entries in App.jsx and net-new
   nav entries in AppLayout.jsx (existing entries untouched).

   This is a placeholder shell: layout + widget slots are ready so real
   data/content can be dropped in later without restructuring the page.
────────────────────────────────────────────────────────────────────────── */

function WidgetCard({ title, subtitle, from, to, icon }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div
          className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
        >
          {icon}
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-300">
          Coming soon
        </span>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
      </div>
      <div className="h-20 rounded-xl border border-dashed border-gray-200 flex items-center justify-center">
        <span className="text-xs text-gray-300">Widget content pending</span>
      </div>
    </div>
  );
}

const PLACEHOLDER_ICON = (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-white" stroke="currentColor" strokeWidth={2}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PLACEHOLDER_WIDGETS = [
  { title: 'Overview', subtitle: 'Summary metrics', from: '#3b82f6', to: '#60a5fa' },
  { title: 'Activity', subtitle: 'Recent activity feed', from: '#7c3aed', to: '#a78bfa' },
  { title: 'Status Breakdown', subtitle: 'By category', from: '#0891b2', to: '#22d3ee' },
  { title: 'Highlights', subtitle: 'Key callouts', from: '#f59e0b', to: '#fbbf24' },
];

export default function BobsDashboard() {
  const user         = useSelector(selectCurrentUser);
  const role         = useSelector(selectUserRole);
  const companyName  = useSelector(selectCompanyName);
  const hasSuperAdminAccess = useSelector(selectHasSuperAdminAccess);

  // Defense-in-depth: this page is only wired into nav for the intended
  // roles, but both /platform/bobs-dashboard and /client/bobs-dashboard
  // are technically reachable by other roles in the same portal group
  // (e.g. PLATFORM_ADMIN) via direct URL — so guard
  // here too, against an exact role allow-list.
  const isAllowed = hasSuperAdminAccess || CLIENT_COMPANY_ROLES.has(role);

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-gray-400">You don't have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">BOB's Dashboard</h1>
        <p className="text-sm text-gray-400 mt-1">
          {companyName ? `${companyName} · ` : ''}
          Welcome{user?.firstName ? `, ${user.firstName}` : ''}. This dashboard is ready for content.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLACEHOLDER_WIDGETS.map((w) => (
          <WidgetCard key={w.title} {...w} icon={PLACEHOLDER_ICON} />
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <p className="text-sm font-semibold text-gray-800 mb-1">Main panel</p>
        <p className="text-xs text-gray-400">
          Reserved space for the primary table/chart once requirements are finalized.
        </p>
        <div className="mt-4 h-48 rounded-xl border border-dashed border-gray-200 flex items-center justify-center">
          <span className="text-xs text-gray-300">Content pending</span>
        </div>
      </div>
    </div>
  );
}
